import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mode } from '@amzn/awsui-global-styles';

import { EMBED_TO_PARENT_MESSAGES, PARENT_TO_EMBED_MESSAGES } from '../constants/embedMessageConstants';
import { useTheme } from '../contexts/ThemeContext';
import { useEmbedParams } from './useEmbedParams';
import { appendEmbedParamsToPath } from '../utils/navigationUtils';
import { logger } from '../utils/logger';

/**
 * Listens for postMessage events from the FinSuite parent window
 * and syncs theme changes into Verity's ThemeContext.
 *
 * Also sends an EMBED_READY signal so FinSuite knows it can start
 * sending messages.
 *
 * IMPORTANT: This hook must be used inside a <BrowserRouter> context
 * so that useNavigate() works for client-side navigation (no full reload).
 */
export const useParentMessageListener = (): void => {
  const { isEmbedded } = useEmbedParams();
  const { setMode } = useTheme();
  const navigate = useNavigate();
  const setModeRef = useRef(setMode);
  const navigateRef = useRef(navigate);

  // Keep refs in sync to avoid stale closures in the listener
  useEffect(() => {
    setModeRef.current = setMode;
  }, [setMode]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!isEmbedded) return;

    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data || {};

      switch (type) {
        case PARENT_TO_EMBED_MESSAGES.THEME_CHANGE: {
          const isDarkMode = payload?.isDarkMode;
          if (typeof isDarkMode === 'boolean') {
            logger?.info('Received theme change from parent:', { isDarkMode });
            setModeRef.current(isDarkMode ? Mode.Dark : Mode.Light);
          }
          break;
        }
        case PARENT_TO_EMBED_MESSAGES.NAVIGATE_HOME: {
          logger?.info('Received navigate home from parent');
          navigateRef.current(appendEmbedParamsToPath('/'));
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    // Signal to FinSuite that we're ready to receive messages
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: EMBED_TO_PARENT_MESSAGES.EMBED_READY }, '*');
      logger?.info('Sent EMBED_READY to parent');
    }

    // Forward clicks to parent so FinSuite can close dropdowns (e.g. search)
    let handleClick: (() => void) | undefined;
    if (isEmbedded) {
      let lastClickForward = 0;
      handleClick = () => {
        const now = Date.now();
        if (now - lastClickForward < 100) return; // Throttle: max once per 100ms
        lastClickForward = now;
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: EMBED_TO_PARENT_MESSAGES.IFRAME_CLICK }, '*');
        }
      };
      document.addEventListener('mousedown', handleClick);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (handleClick) document.removeEventListener('mousedown', handleClick);
    };
  }, [isEmbedded]);
};
